import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSnackbar } from "./snackbarProvider.jsx";
import useGraphQLClient from "../hooks/useGraphQLClient";
import { CREATE_INTERACTION } from "../graph/interactions/mutations";
import {
  interactionTypeOptions,
  interactionDirectionOptions,
  valueActionOptions,
  interactionTypeValues,
  interactionDirectionValues,
  valueActionValues,
} from "../config/exportFormOptions.js";

const CreateInteractionModal = ({ open, onClose, onSuccess, contact }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();

  const handleFormSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const input = {
        contactID: values.contactID,
        type: values.type,
        direction: values.direction,
        summary: values.summary || null,
        notes: values.notes || null,
        meaningful: values.meaningful,
        valueAction: values.valueAction || null,
      };

      await client.request(CREATE_INTERACTION, { input });

      setSnackbar({
        open: true,
        message: "Interaction created successfully!",
        severity: "success",
      });

      resetForm();
      onClose();

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("GraphQL Error:", err);
      console.error("GraphQL response errors:", err?.response?.errors);

      setSnackbar({
        open: true,
        message: `Failed to create interaction: ${message}`,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Add Interaction
        {contact ? ` — ${contact.firstName || ""} ${contact.lastName || ""}` : ""}
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          contactID: contact?.id || "",
          type: "",
          direction: "",
          summary: "",
          notes: "",
          meaningful: true,
          valueAction: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  pt: 1,
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  label="Contact"
                  value={
                    contact
                      ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim()
                      : ""
                  }
                  disabled
                  sx={{ gridColumn: "span 4" }}
                />

                <FormControl
                  fullWidth
                  variant="filled"
                  sx={{ gridColumn: "span 2" }}
                  error={!!touched.type && !!errors.type}
                >
                  <InputLabel>Interaction Type</InputLabel>
                  <Select
                    name="type"
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {interactionTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  variant="filled"
                  sx={{ gridColumn: "span 2" }}
                  error={!!touched.direction && !!errors.direction}
                >
                  <InputLabel>Direction</InputLabel>
                  <Select
                    name="direction"
                    value={values.direction}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {interactionDirectionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  variant="filled"
                  sx={{ gridColumn: "span 4" }}
                  error={!!touched.valueAction && !!errors.valueAction}
                >
                  <InputLabel>Value Action</InputLabel>
                  <Select
                    name="valueAction"
                    value={values.valueAction}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">None</MenuItem>
                    {valueActionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  variant="filled"
                  label="Summary"
                  name="summary"
                  value={values.summary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.summary && !!errors.summary}
                  helperText={touched.summary && errors.summary}
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Notes"
                  name="notes"
                  multiline
                  minRows={4}
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.notes && !!errors.notes}
                  helperText={touched.notes && errors.notes}
                  sx={{ gridColumn: "span 4" }}
                />

                <Box sx={{ gridColumn: "span 4" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.meaningful}
                        onChange={(e) => setFieldValue("meaningful", e.target.checked)}
                      />
                    }
                    label="Meaningful interaction"
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="primary" variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                Create Interaction
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const validationSchema = yup.object().shape({
  contactID: yup.string().required("Required"),
  type: yup.string().oneOf(interactionTypeValues).required("Required"),
  direction: yup.string().oneOf(interactionDirectionValues).required("Required"),
  valueAction: yup.string().oneOf(["", ...valueActionValues]).nullable().notRequired(),
  summary: yup.string().required("Required"),
  notes: yup.string().nullable().notRequired(),
  meaningful: yup.boolean().required(),
});

export default CreateInteractionModal;